// features/workstations/property-dossier/investor-profile.js
// ═══════════════════════════════════════════════════════════
// Investor Profile editor for the Deal Dossier. Sets the tax-
// personalization inputs the analyzer reads from
// workstation.config.investor_profile:
//   reps_eligible · tier · marginal_rate · state · entity
//
// reps_eligible is the big lever — it flips the REPS unlock from
// $0 to real active-income tax savings in every dossier.
//
// Persists via PUT /api/workstations/[slug] { config }. The dossier
// API re-reads config from the DB per analysis, so a save here
// personalizes the very next dossier.
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';

const TIERS = ['T1', 'T2', 'T3', 'T4', 'T5'];
const labelStyle = { display: 'block', fontSize: 11, color: '#888', marginBottom: 5 };
const inputStyle = {
  width: '100%', background: 'var(--surface-3)', border: '1px solid var(--border-2)',
  borderRadius: 8, padding: '8px 10px', color: 'inherit', fontSize: 13,
};

export default function InvestorProfile({ workstation, userId }) {
  const initial = (workstation?.config?.investor_profile) || {};
  const [expanded, setExpanded] = useState(false);
  const [tier, setTier] = useState(initial.tier || 'T2');
  const [reps, setReps] = useState(!!initial.reps_eligible);
  const [rate, setRate] = useState(
    Number.isFinite(initial.marginal_rate) ? Math.round(initial.marginal_rate * 100) : ''
  );
  const [stateCode, setStateCode] = useState(initial.state || '');
  const [entity, setEntity] = useState(initial.entity || '');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);

  const summary = [
    tier,
    reps ? 'REPS eligible' : 'no REPS',
    stateCode || null,
    entity || null,
  ].filter(Boolean).join(' · ');

  const save = async () => {
    if (!workstation?.slug || !userId) { setError('Missing workstation context'); return; }
    setSaving(true);
    setError(null);
    try {
      const investor_profile = {
        tier,
        reps_eligible: reps,
        state: stateCode.trim().toUpperCase() || null,
        entity: entity.trim() || null,
      };
      const r = Number(rate);
      if (Number.isFinite(r) && r > 0) investor_profile.marginal_rate = r > 1 ? r / 100 : r;

      const config = { ...(workstation.config || {}), investor_profile };
      const res = await fetch(`/api/workstations/${encodeURIComponent(workstation.slug)}?user_id=${encodeURIComponent(userId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      // keep the in-memory prop fresh so the summary reflects the save
      if (workstation) workstation.config = config;
      setSavedAt(Date.now());
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border-3)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, maxWidth: 820 }}>
      <button
        onClick={() => setExpanded((v) => !v)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0 }}
      >
        <span style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: '#888' }}>
          Investor profile <span style={{ color: '#bbb', textTransform: 'none', letterSpacing: 0, marginLeft: 8 }}>{summary}</span>
        </span>
        <span style={{ fontSize: 12, color: '#888' }}>{expanded ? '▲' : '▼ edit'}</span>
      </button>

      {expanded && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12 }}>
            <div>
              <label style={labelStyle}>Tier</label>
              <select style={inputStyle} value={tier} onChange={(e) => setTier(e.target.value)}>
                {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Marginal tax rate %</label>
              <input style={inputStyle} type="number" inputMode="numeric" value={rate}
                onChange={(e) => setRate(e.target.value)} placeholder="32" />
            </div>
            <div>
              <label style={labelStyle}>State</label>
              <input style={inputStyle} value={stateCode} onChange={(e) => setStateCode(e.target.value)} placeholder="FL" maxLength={2} />
            </div>
            <div>
              <label style={labelStyle}>Holding entity</label>
              <input style={inputStyle} value={entity} onChange={(e) => setEntity(e.target.value)} placeholder="LLC" />
            </div>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={reps} onChange={(e) => setReps(e.target.checked)} />
            <span style={{ fontSize: 13 }}>
              <b>Real Estate Professional Status (REPS) eligible</b>
              <span style={{ color: '#888' }}> — lets rental losses offset active income (the biggest tax lever)</span>
            </span>
          </label>

          {error && <div style={{ color: '#c46050', fontSize: 12, marginTop: 10 }}>{error}</div>}

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
            <button onClick={save} disabled={saving}
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#c8a96e', color: '#1a1a1a', fontWeight: 600, fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Saving…' : 'Save profile'}
            </button>
            {savedAt && <span style={{ fontSize: 12, color: '#7a9e82' }}>Saved ✓ — applies to your next analysis</span>}
          </div>
        </div>
      )}
    </div>
  );
}
